// src/components/LiveEditAdminBar.js
import {
    ChevronDown,
    ChevronRight,
    Edit,
    ExternalLink,
    Menu,
    Plus,
    Settings,
    X
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const LiveEditAdminBar = ({ page, sections = [] }) => {
    const { isAdmin, user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);
    const [showSections, setShowSections] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    // All hooks must be called before any conditional returns
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Don't show the admin bar if user is not admin
    if (!isAdmin || !user) {
        return null;
    }

    // Don't show on admin pages
    if (location.pathname.startsWith("/pitech/")) {
        return null;
    }

    // Get top-level sections (not children of containers)
    const topLevelSections = sections.filter((section) => !section.parentId);

    // Function to get child sections for containers
    const getChildSections = (parentId) => {
        return sections
            .filter(
                (section) =>
                    section.parentId &&
                    section.parentId.toString() === parentId.toString()
            )
            .sort((a, b) => a.order - b.order);
    };

    // Navigate to edit page
    const editPage = () => {
        if (page && page._id) {
            navigate(`/pitech/pages/${page._id}#page`);
        }
    };

    // Navigate to edit specific section
    const editSection = (sectionId) => {
        if (page && page._id) {
            navigate(`/pitech/pages/${page._id}#section-${sectionId}`);
        }
    };

    // Navigate to add new section
    const addSection = () => {
        if (page && page._id) {
            navigate(`/pitech/pages/${page._id}#add-section`);
        }
    };

    // Navigate to page management
    const managePages = () => {
        navigate("/pitech/pages");
    };

    // Navigate to admin dashboard
    const goToDashboard = () => {
        navigate("/pitech");
    };

    // Toggle preview mode (could be used to hide/show edit overlays)
    const togglePreview = () => {
        // This could toggle a global state for showing edit hints on sections
        console.log("Toggle preview mode");
    };

    // Render section item in dropdown
    const renderSectionItem = (section, index, depth = 0) => {
        const childSections =
            section.type === "container" ? getChildSections(section._id) : [];
        const hasChildren = childSections.length > 0;

        return (
            <div key={section._id}>
                <button
                    onClick={() => editSection(section._id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center"
                    style={{ paddingLeft: `${12 + depth * 16}px` }}
                >
                    <Edit className="w-3 h-3 mr-2 text-gray-500" />
                    <span className="font-medium mr-2">{index + 1}.</span>
                    <span className="capitalize">
                        {section.type.replace(/([A-Z])/g, " $1").trim()}
                    </span>
                    {section.content?.title && (
                        <span className="ml-2 text-gray-500 truncate">
                            - {section.content.title.substring(0, 20)}...
                        </span>
                    )}
                </button>

                {/* Render child sections for containers */}
                {hasChildren && (
                    <div className="bg-gray-50">
                        {childSections.map((childSection, childIndex) =>
                            renderSectionItem(childSection, childIndex, depth + 1)
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (!isVisible) {
        // Show minimal toggle button when hidden
        return (
            <div className="fixed top-0 left-0 z-50 bg-gray-800 text-white p-2 rounded-br-lg shadow-lg">
                <button
                    onClick={() => setIsVisible(true)}
                    className="flex items-center text-sm"
                >
                    <Menu className="w-4 h-4 mr-1" />
                    Edit
                </button>
            </div>
        );
    }

    return (
        <>
            {/* Main Admin Bar */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-gray-800 text-white shadow-lg">
                <div className="flex items-center justify-between px-4 py-2">
                    {/* Left side - Main actions */}
                    <div className="flex items-center space-x-4">
                        {/* Site title/logo */}
                        <div className="flex items-center">
                            <img
                                src="/images/piTech-logo.png"
                                alt="Admin"
                                className="h-6 w-12 mr-2"
                            />
                            {!isMobile && (
                                <span className="text-sm font-medium">Live Edit Mode</span>
                            )}
                        </div>

                        {/* Page actions */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={editPage}
                                className="flex items-center px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                            >
                                <Settings className="w-4 h-4 mr-1" />
                                {!isMobile ? "Edit Page" : "Edit"}
                            </button>

                            <button
                                onClick={addSection}
                                className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm transition-colors"
                            >
                                <Plus className="w-4 h-4 mr-1" />
                                {!isMobile ? "Add Section" : "Add"}
                            </button>

                            {/* Sections dropdown */}
                            {topLevelSections.length > 0 && (
                                <div className="relative">
                                    <button
                                        onClick={() => setShowSections(!showSections)}
                                        className="flex items-center px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors"
                                    >
                                        <Edit className="w-4 h-4 mr-1" />
                                        {!isMobile ? "Sections" : "Sections"}
                                        {showSections ? (
                                            <ChevronDown className="w-4 h-4 ml-1" />
                                        ) : (
                                            <ChevronRight className="w-4 h-4 ml-1" />
                                        )}
                                    </button>

                                    {/* Sections dropdown menu */}
                                    {showSections && (
                                        <div className="absolute top-full left-0 mt-1 bg-white text-gray-800 rounded shadow-lg border min-w-64 max-h-96 overflow-y-auto">
                                            <div className="py-1">
                                                <div className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-50 border-b">
                                                    Edit Sections ({topLevelSections.length})
                                                </div>
                                                {topLevelSections
                                                    .sort((a, b) => a.order - b.order)
                                                    .map((section, index) =>
                                                        renderSectionItem(section, index)
                                                    )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right side - Secondary actions */}
                    <div className="flex items-center space-x-2">
                        {!isMobile && (
                            <>
                                <button
                                    onClick={managePages}
                                    className="flex items-center px-2 py-1 hover:bg-gray-700 rounded text-sm transition-colors"
                                >
                                    <ExternalLink className="w-4 h-4 mr-1" />
                                    All Pages
                                </button>

                                <button
                                    onClick={goToDashboard}
                                    className="flex items-center px-2 py-1 hover:bg-gray-700 rounded text-sm transition-colors"
                                >
                                    <Settings className="w-4 h-4 mr-1" />
                                    Dashboard
                                </button>
                            </>
                        )}

                        {/* Mobile menu for secondary actions */}
                        {isMobile && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowSections(!showSections)}
                                    className="p-1 hover:bg-gray-700 rounded"
                                >
                                    <Menu className="w-4 h-4" />
                                </button>

                                {showSections && (
                                    <div className="absolute top-full right-0 mt-1 bg-white text-gray-800 rounded shadow-lg border min-w-48">
                                        <div className="py-1">
                                            <button
                                                onClick={managePages}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center"
                                            >
                                                <ExternalLink className="w-4 h-4 mr-2" />
                                                All Pages
                                            </button>
                                            <button
                                                onClick={goToDashboard}
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center"
                                            >
                                                <Settings className="w-4 h-4 mr-2" />
                                                Dashboard
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Hide button */}
                        <button
                            onClick={() => setIsVisible(false)}
                            className="p-1 hover:bg-gray-700 rounded"
                            title="Hide admin bar"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Page info bar */}
                {page && (
                    <div className="bg-gray-700 px-4 py-1 text-xs border-t border-gray-600">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <span>
                                    <strong>Page:</strong> {page.title}
                                </span>
                                <span>
                                    <strong>Slug:</strong> /{page.slug}
                                </span>
                                <span
                                    className={`px-2 py-0.5 rounded text-xs ${page.isPublished
                                        ? "bg-green-100 text-green-800"
                                        : "bg-yellow-100 text-yellow-800"
                                        }`}
                                >
                                    {page.isPublished ? "Published" : "Draft"}
                                </span>
                            </div>

                            {!isMobile && (
                                <div className="text-gray-300">
                                    Sections: {topLevelSections.length}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Spacer to push content down */}
            <div style={{ height: page ? "80px" : "60px" }} />

            {/* Click outside to close dropdowns */}
            {showSections && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowSections(false)}
                />
            )}
        </>
    );
};

export default LiveEditAdminBar;
